/**
 * Format Algolia product data.
 * @param {Object} response - Algolia item data.
 * @returns {Object} formatted data.
 */
export function formatAlgoliaProduct(response) {
  const product = response

  let url = `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`

  if (product['__queryID']) {
    url = `${url}?queryID=${product['__queryID']}`
  }

  const formattedProduct = {
    ...product,
    available: product.available,
    compareAtPrice: formatAlgoliaPrice(product, 'compareAtPrice'),
    description: localiseString(product, 'description'),
    handle: product.handle,
    id: product.id,
    images: formatAlgoliaImages(product),
    metafields: [
      {
        name: 'swatch_code',
        type: 'color' || false,
        value: product?.metafields?.['colour.colour_swatch_code'] || false,
      },
      {
        name: 'swatch_name',
        type: 'single_line_text_field' || false,
        value: product?.metafields?.['colour.colour_swatch_name'] || false,
      },
    ],
    isComingSoon: (product?.tags || []).includes('coming-soon'),
    options: null,
    price: formatAlgoliaPrice(product, 'price'),
    tags: product.tags,
    title: localiseString(product, 'title'),
    inventory: product.inventory_quantity,
    type: product.productType,
    url,
    variantId: product.objectID ? Number(product.objectID) : false,
    vendor: product.vendor,
  }

  formattedProduct.featuredImage = formattedProduct.images[0]

  return formattedProduct
}

/**
 * Format Algolia product price.
 * @param {Object} product - Algolia item data.
 * @param {String} type - `price` or `compareAtPrice`.
 * @returns {Object} formatted price data.
 */
function formatAlgoliaPrice(product, type = 'price') {
  const price = product[type][window.cnvs.store.marketHandle]

  return {
    amount: formatPriceAmount(price?.amount),
    currencyCode: price?.currencyCode,
    max: formatPriceAmount(price?.max),
    min: formatPriceAmount(price?.min),
    varies: price?.max !== price?.min,
  }
}

/**
 * Format Algolia image data.
 * @param {Object} product - Algolia item data.
 * @returns {Array} formatted image data as an array of objects.
 */
function formatAlgoliaImages(product) {
  if(!product?.images) {
    return []
  }

  return product.images.map((image) => {
    return {
      alt: image.alt,
      id: image.id,
      src: image.src,
    }
  })
}

/**
 * Format a price amount
 * - If the amount can be converted to a number, it is converted and multiplied
 *   by 100 to get the cents amount
 * - Otherwise, returns `false`
 * @param {unknown} amount - amount value to process
 * @returns {Number|false} formatted number or `false`
 */
function formatPriceAmount(amount) {
  // eslint-disable-next-line no-undefined
  if (amount === null || amount === undefined) {
    return false
  }

  const numericAmount = Number(amount)

  if (isNaN(numericAmount)) {
    return false
  }

  return numericAmount * 100
}

/**
 * Localise string based on Algolia response and current language.
 * - If primary language then just use product response value.
 * - Otherwise find string in translations array for language.
 * @param {Object} product - Algolia response.
 * @param {String} property - Property on product response.
 */
function localiseString(product, property) {
  if (window.cnvs.store.languagePrimary) {
    return product[property]
  }

  const language = window.cnvs.store.language?.toUpperCase()
  const translations = product[`translations${language}`]

  /**
   * Map string to corresponding key returned in translations array.
   */
  let key = property

  switch (key) {
    case 'description':
      key = 'body_html'
      break
  }

  /**
   * Use translation if it exists, otherwise use product object's property.
   */
  return translations[key]
    ? translations[key]
    : product[property]
}

/*
 * Generates badge object from product.
 * @param {Object} - Formatted product.
 * @returns {Object}
 */
export function generateProductBadge(product) {
  const strings = window.cnvs.strings.product.badges
  const badge = product.tags?.find((tag) => tag.toLowerCase().includes('badge:')) || ''
  let [
    // eslint-disable-next-line prefer-const
    label = '', color = '#111111', bgColor = '#F9F9F9',
  ] = badge.replace(/badge:/i, '').split(':').map((item) => item.trim())

  /**
   * Handle coming soon tagged products.
   */
  if (label.includes('coming-soon')) {
    return {
      label: strings.coming_soon,
      color,
      bgColor,
      type: 'badge',
    }
  }

  /**
   * If no badge or label then return false.
   */
  if (!badge || !label) {
    return {
      label: false,
      color: false,
      bgColor: false,
      type: false,
    }
  }

  let type = 'badge'

  /**
   * Handle common strings.
   */
  switch (label.toLowerCase()) {
    case 'bestseller':
    case 'best seller':
      label = strings.bestseller
      break
    case 'coming soon':
      label = strings.coming_soon
      break
    case 'sale':
      label = strings.sale
      type = 'sale'
      break
    case 'new':
    case 'new in':
      label = strings.new_in
      break
    case 'sale':
      label = strings.sale
      break
  }

  /**
   * Handle #% off strings.
   */
  if (label.match(/(?<value>\d+)% off/gi)) {
    label = label.replace(/(?<value>\d+)% off/gi, (_, $1) => {
      return strings.percent_off.replace('{{ percent }}', $1)
    })
  }

  /**
   * Return object.
   */
  return {
    label,
    color,
    bgColor,
    type,
  }
}
